import {
    ArrowDownIcon,
    ArrowUpIcon,
    CheckCircleIcon,
    PencilIcon,
    PhotoIcon,
    PlusIcon,
    TrashIcon,
    XCircleIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import { useCallback, useEffect, useState } from "react";
import apiService, { adminService } from "../../services/api";

const BannerManagement = () => {
    const [activeTab, setActiveTab] = useState("home-hero");
    const [homeHeroBanners, setHomeHeroBanners] = useState([]);
    const [categoryBanners, setCategoryBanners] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingBanner, setEditingBanner] = useState(null);
    const [reordering, setReordering] = useState(false);

    const fetchBanners = useCallback(async () => {
        try {
            setLoading(true);
            const cacheKeys = [];
            apiService.cache.forEach((value, key) => {
                if (key.includes("/admin/banners")) {
                    cacheKeys.push(key);
                }
            });
            cacheKeys.forEach(key => apiService.cache.delete(key));

            const [homeHeroResponse, categoryResponse, categoriesResponse] = await Promise.all([
                adminService.getAllBanners({ type: "home-hero" }),
                adminService.getAllBanners({ type: "category" }),
                adminService.getActiveCategories(),
            ]);

            if (homeHeroResponse.status === "success") {
                setHomeHeroBanners(homeHeroResponse.data.banners);
            }
            if (categoryResponse.status === "success") {
                setCategoryBanners(categoryResponse.data.banners);
            }
            if (categoriesResponse.status === "success") {
                setCategories(categoriesResponse.data.categories);
            }
        } catch (error) {
            console.error("Failed to fetch banners:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchBanners();
    }, [fetchBanners]);

    const handleDeleteBanner = async (bannerId) => {
        if (window.confirm("Are you sure you want to delete this banner?")) {
            try {
                await adminService.deleteBanner(bannerId);
                fetchBanners();
            } catch (error) {
                console.error("Failed to delete banner:", error);
                alert(error.response?.data?.message || "Failed to delete banner");
            }
        }
    };

    const handleEditBanner = (banner) => {
        setEditingBanner(banner);
        setShowModal(true);
    };

    const handleCreateBanner = () => {
        setEditingBanner(null);
        setShowModal(true);
    };

    const handleReorder = async (bannerId, direction) => {
        const banners = activeTab === "home-hero" ? homeHeroBanners : categoryBanners;
        const currentIndex = banners.findIndex((b) => b._id === bannerId);
        if (currentIndex === -1) return;

        const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
        if (newIndex < 0 || newIndex >= banners.length) return;

        const newBanners = [...banners];
        [newBanners[currentIndex], newBanners[newIndex]] = [newBanners[newIndex], newBanners[currentIndex]];

        if (activeTab === "home-hero") {
            setHomeHeroBanners(newBanners);
        } else {
            setCategoryBanners(newBanners);
        }

        try {
            setReordering(true);
            const bannerIds = newBanners.map((b) => b._id);
            await adminService.reorderBanners(bannerIds);
            await fetchBanners();
        } catch (error) {
            console.error("Failed to reorder banners:", error);
            alert(error.response?.data?.message || "Failed to reorder banners");
            fetchBanners();
        } finally {
            setReordering(false);
        }
    };

    const renderHomeHeroBanners = () => {
        if (loading) {
            return (
                <div className="flex justify-center items-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#008000]"></div>
                </div>
            );
        }

        return (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gradient-to-r from-green-50 to-emerald-50">
                            <tr>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Order</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Desktop Image</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Mobile Image</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Headline</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {homeHeroBanners.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                                        No home hero banners found. Create your first banner!
                                    </td>
                                </tr>
                            ) : (
                                homeHeroBanners.map((banner, index) => (
                                    <tr key={banner._id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col items-center space-y-1">
                                                <span className="text-sm font-medium text-gray-600">{index + 1}</span>
                                                <div className="flex flex-col space-y-1">
                                                    <button
                                                        onClick={() => handleReorder(banner._id, "up")}
                                                        disabled={index === 0 || reordering}
                                                        className={`p-1 rounded transition-colors ${index === 0 || reordering
                                                            ? "text-gray-300 cursor-not-allowed"
                                                            : "text-[#008000] hover:bg-green-50"
                                                            }`}
                                                    >
                                                        <ArrowUpIcon className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleReorder(banner._id, "down")}
                                                        disabled={index === homeHeroBanners.length - 1 || reordering}
                                                        className={`p-1 rounded transition-colors ${index === homeHeroBanners.length - 1 || reordering
                                                            ? "text-gray-300 cursor-not-allowed"
                                                            : "text-[#008000] hover:bg-green-50"
                                                            }`}
                                                    >
                                                        <ArrowDownIcon className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {banner.image?.url ? (
                                                <img
                                                    src={banner.image.url}
                                                    alt="Desktop"
                                                    className="w-24 h-16 object-cover rounded-lg border border-gray-200"
                                                />
                                            ) : (
                                                <div className="w-24 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                                                    <PhotoIcon className="w-8 h-8 text-gray-400" />
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            {banner.mobileImage?.url ? (
                                                <img
                                                    src={banner.mobileImage.url}
                                                    alt="Mobile"
                                                    className="w-16 h-24 object-cover rounded-lg border border-gray-200"
                                                />
                                            ) : (
                                                <div className="w-16 h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                                                    <PhotoIcon className="w-8 h-8 text-gray-400" />
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="max-w-xs">
                                                <p className="font-medium text-gray-900 truncate">{banner.headline}</p>
                                                <p className="text-sm text-gray-500 truncate">{banner.subheadline}</p>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {banner.isActive ? (
                                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-[#008000]">
                                                    <CheckCircleIcon className="w-4 h-4 mr-1" />
                                                    Active
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                    <XCircleIcon className="w-4 h-4 mr-1" />
                                                    Inactive
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={() => handleEditBanner(banner)}
                                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                >
                                                    <PencilIcon className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteBanner(banner._id)}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                >
                                                    <TrashIcon className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderCategoryBanners = () => {
        if (loading) {
            return (
                <div className="flex justify-center items-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#008000]"></div>
                </div>
            );
        }

        const categoryBannerMap = {};
        categoryBanners.forEach((banner) => {
            if (banner.category) {
                categoryBannerMap[banner.category._id || banner.category] = banner;
            }
        });

        return (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gradient-to-r from-green-50 to-emerald-50">
                            <tr>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Category</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Desktop Image</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Mobile Image</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Headline</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {categories.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                                        No categories found.
                                    </td>
                                </tr>
                            ) : (
                                categories.map((category) => {
                                    const banner = categoryBannerMap[category._id];
                                    return (
                                        <tr key={category._id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                <span className="font-medium text-gray-900 capitalize">{category.name}</span>
                                            </td>
                                            <td className="px-6 py-4">
                                                {banner?.image?.url ? (
                                                    <img
                                                        src={banner.image.url}
                                                        alt="Desktop"
                                                        className="w-24 h-16 object-cover rounded-lg border border-gray-200"
                                                    />
                                                ) : (
                                                    <div className="w-24 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                                                        <PhotoIcon className="w-8 h-8 text-gray-400" />
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                {banner?.mobileImage?.url ? (
                                                    <img
                                                        src={banner.mobileImage.url}
                                                        alt="Mobile"
                                                        className="w-16 h-24 object-cover rounded-lg border border-gray-200"
                                                    />
                                                ) : (
                                                    <div className="w-16 h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                                                        <PhotoIcon className="w-8 h-8 text-gray-400" />
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="max-w-xs">
                                                    <p className="font-medium text-gray-900 truncate">
                                                        {banner?.headline || "No banner"}
                                                    </p>
                                                    <p className="text-sm text-gray-500 truncate">
                                                        {banner?.subheadline || ""}
                                                    </p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                {banner?.isActive ? (
                                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-[#008000]">
                                                        <CheckCircleIcon className="w-4 h-4 mr-1" />
                                                        Active
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                        <XCircleIcon className="w-4 h-4 mr-1" />
                                                        {banner ? "Inactive" : "No Banner"}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <button
                                                    onClick={() => {
                                                        if (banner) {
                                                            const bannerId = banner._id || banner.id;
                                                            handleEditBanner({ ...banner, _id: bannerId, type: banner.type || "category", category: category._id });
                                                        } else {
                                                            handleEditBanner({ type: "category", category: category._id });
                                                        }
                                                    }}
                                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                >
                                                    <PencilIcon className="w-5 h-5" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <div className="p-6 space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-3xl font-bold bg-gradient-to-r from-[#008000] to-emerald-600 bg-clip-text text-transparent">
                        Banner Management
                    </h2>
                    <p className="text-gray-600 mt-1">Manage home page hero banners and category page banners</p>
                </div>
                {activeTab === "home-hero" && (
                    <button
                        onClick={handleCreateBanner}
                        className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-[#008000] to-emerald-600 text-white rounded-xl hover:from-[#008000] hover:to-emerald-700 transition-colors shadow-lg hover:shadow-xl"
                    >
                        <PlusIcon className="w-5 h-5" />
                        <span>Add Banner</span>
                    </button>
                )}
            </div>

            <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                    <button
                        onClick={() => setActiveTab("home-hero")}
                        className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === "home-hero"
                            ? "border-[#008000] text-[#008000]"
                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                            }`}
                    >
                        Home Hero Banners
                    </button>
                    <button
                        onClick={() => setActiveTab("category")}
                        className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === "category"
                            ? "border-[#008000] text-[#008000]"
                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                            }`}
                    >
                        Category Banners
                    </button>
                </nav>
            </div>

            {activeTab === "home-hero" ? renderHomeHeroBanners() : renderCategoryBanners()}

            {showModal && (
                <BannerModal
                    banner={editingBanner}
                    type={activeTab}
                    categories={categories}
                    onClose={() => setShowModal(false)}
                    onSave={async () => {
                        setShowModal(false);
                        fetchBanners();
                    }}
                />
            )}
        </div>
    );
};

const BannerModal = ({ banner, type, categories, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        type: banner?.type || (type === "home-hero" ? "home-hero" : "category"),
        category: banner?.category?._id || banner?.category || (type === "category" && categories[0]?._id) || "",
        headline: banner?.headline || "",
        subheadline: banner?.subheadline || "",
        buttonText: banner?.buttonText || "",
        buttonLink: banner?.buttonLink || "",
        navLinks: banner?.navLinks || [],
        isActive: banner?.isActive ?? true,
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(banner?.image?.url || null);
    const [mobileImageFile, setMobileImageFile] = useState(null);
    const [mobileImagePreview, setMobileImagePreview] = useState(banner?.mobileImage?.url || null);
    const [navLinkInput, setNavLinkInput] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (banner) {
            setFormData({
                type: banner.type || (type === "home-hero" ? "home-hero" : "category"),
                category: banner.category?._id || banner.category || "",
                headline: banner.headline || "",
                subheadline: banner.subheadline || "",
                buttonText: banner.buttonText || "",
                buttonLink: banner.buttonLink || "",
                navLinks: banner.navLinks || [],
                isActive: banner.isActive ?? true,
            });
            setImagePreview(banner.image?.url || null);
            setMobileImagePreview(banner.mobileImage?.url || null);
            setImageFile(null);
            setMobileImageFile(null);
        } else {
            setFormData({
                type: type === "home-hero" ? "home-hero" : "category",
                category: type === "category" && categories[0]?._id ? categories[0]._id : "",
                headline: "",
                subheadline: "",
                buttonText: "",
                buttonLink: "",
                navLinks: [],
                isActive: true,
            });
            setImagePreview(null);
            setMobileImagePreview(null);
            setImageFile(null);
            setMobileImageFile(null);
        }
    }, [banner, type, categories]);

    const handleImageChange = (e, isMobile = false) => {
        const file = e.target.files[0];
        if (file) {
            if (isMobile) {
                setMobileImageFile(file);
                const reader = new FileReader();
                reader.onloadend = () => {
                    setMobileImagePreview(reader.result);
                };
                reader.readAsDataURL(file);
            } else {
                setImageFile(file);
                const reader = new FileReader();
                reader.onloadend = () => {
                    setImagePreview(reader.result);
                };
                reader.readAsDataURL(file);
            }
        }
    };

    const handleAddNavLink = () => {
        if (navLinkInput.trim()) {
            setFormData({
                ...formData,
                navLinks: [...formData.navLinks, navLinkInput.trim()],
            });
            setNavLinkInput("");
        }
    };

    const handleRemoveNavLink = (index) => {
        setFormData({
            ...formData,
            navLinks: formData.navLinks.filter((_, i) => i !== index),
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSaving(true);

        try {
            const submitData = { ...formData };

            const bannerId = banner?._id || banner?.id;
            if (banner && bannerId) {
                submitData.type = banner.type || formData.type;
                if (formData.type === "category" && formData.category) {
                    submitData.category = String(formData.category);
                }
                await adminService.updateBanner(bannerId, submitData, imageFile, mobileImageFile);
            } else {
                if (formData.type === "category" && formData.category) {
                    submitData.category = String(formData.category);
                }
                await adminService.createBanner(submitData, imageFile, mobileImageFile);
            }
            onSave();
        } catch (error) {
            console.error("Failed to save banner:", error);
            setError(error.response?.data?.message || error.message || "Failed to save banner");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="text-2xl font-bold text-gray-900">
                        {banner ? "Edit Banner" : "Create Banner"}
                    </h3>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <XMarkIcon className="w-6 h-6 text-gray-500" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                            {error}
                        </div>
                    )}

                    {formData.type === "category" && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Category *
                            </label>
                            <select
                                value={formData.category}
                                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                required
                            >
                                <option value="">Select a category</option>
                                {categories.map((cat) => (
                                    <option key={cat._id} value={cat._id}>
                                        {cat.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Desktop Image *
                        </label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageChange(e, false)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                        {imagePreview && (
                            <img
                                src={imagePreview}
                                alt="Preview"
                                className="mt-4 w-full max-w-md h-48 object-cover rounded-lg border border-gray-200"
                            />
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Mobile Image (Optional)
                        </label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageChange(e, true)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                        {mobileImagePreview && (
                            <img
                                src={mobileImagePreview}
                                alt="Mobile Preview"
                                className="mt-4 w-full max-w-md h-64 object-cover rounded-lg border border-gray-200"
                            />
                        )}
                    </div>

                    {formData.type === "home-hero" && (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Headline *
                                </label>
                                <input
                                    type="text"
                                    value={formData.headline}
                                    onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Subheadline *
                                </label>
                                <textarea
                                    value={formData.subheadline}
                                    onChange={(e) => setFormData({ ...formData, subheadline: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    rows="3"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Button Text
                                </label>
                                <input
                                    type="text"
                                    value={formData.buttonText}
                                    onChange={(e) => setFormData({ ...formData, buttonText: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Button Link
                                </label>
                                <input
                                    type="text"
                                    value={formData.buttonLink}
                                    onChange={(e) => setFormData({ ...formData, buttonLink: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Navigation Links
                                </label>
                                <div className="flex space-x-2 mb-2">
                                    <input
                                        type="text"
                                        value={navLinkInput}
                                        onChange={(e) => setNavLinkInput(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                handleAddNavLink();
                                            }
                                        }}
                                        placeholder="Add navigation link"
                                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleAddNavLink}
                                        className="px-4 py-2 bg-[#008000] text-white rounded-lg hover:bg-[#008000] transition-colors"
                                    >
                                        Add
                                    </button>
                                </div>
                                <div className="space-y-2">
                                    {formData.navLinks.map((link, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center justify-between bg-gray-50 px-4 py-2 rounded-lg"
                                        >
                                            <span>{link}</span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveNavLink(index)}
                                                className="text-red-600 hover:text-red-700"
                                            >
                                                <XMarkIcon className="w-5 h-5" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}

                    {formData.type === "category" && (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Headline
                                </label>
                                <input
                                    type="text"
                                    value={formData.headline}
                                    onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Subheadline
                                </label>
                                <textarea
                                    value={formData.subheadline}
                                    onChange={(e) => setFormData({ ...formData, subheadline: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    rows="3"
                                />
                            </div>
                        </>
                    )}

                    <div>
                        <label className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                checked={formData.isActive}
                                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                className="w-4 h-4 text-[#008000] border-gray-300 rounded focus:ring-green-500"
                            />
                            <span className="text-sm font-medium text-gray-700">Active</span>
                        </label>
                    </div>

                    <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="px-6 py-2 bg-gradient-to-r from-[#008000] to-emerald-600 text-white rounded-lg hover:from-[#008000] hover:to-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving ? "Saving..." : banner ? "Update Banner" : "Create Banner"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default BannerManagement;

